import React from 'react';
import { PROCUREMENT_FEATURES, ProcurementFeature, ProcurementPlanConfig } from '../api/procurementAdmin';
import { useLanguage } from '../context/LanguageContext';

export default function ProcurementPlanEditor({ value, onChange, disabled = false }: {
  value: ProcurementPlanConfig; onChange: (value: ProcurementPlanConfig) => void; disabled?: boolean;
}) {
  const { t } = useLanguage();
  const toggle = (key: ProcurementFeature, checked: boolean) => {
    const features = { ...value.features, [key]: checked };
    if (checked && ['lot_analytics', 'possible_suppliers', 'search_alerts'].includes(key)) features.lots = true;
    if (checked && key === 'supplier_analytics') features.suppliers = true;
    if (!checked && key === 'lots') {
      features.lot_analytics = false; features.possible_suppliers = false; features.search_alerts = false;
    }
    if (!checked && key === 'suppliers') features.supplier_analytics = false;
    onChange({ ...value, features });
  };
  return <fieldset disabled={disabled} className="min-w-0 max-w-full space-y-5 rounded-2xl border border-sky-200 bg-sky-50/40 p-3 disabled:opacity-60 sm:p-4">
    <legend className="max-w-full px-2 text-sm font-bold text-slate-800">{t('procurementAdmin.services')}</legend>
    <div className="grid min-w-0 gap-2 sm:grid-cols-2">{PROCUREMENT_FEATURES.map(key => <label key={key} className={`flex min-h-[44px] min-w-0 cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 text-sm leading-5 ${value.features[key] ? 'border-sky-200 bg-white text-slate-900' : 'border-transparent text-slate-600 hover:bg-white/70'}`}>
      <input type="checkbox" checked={!!value.features[key]} onChange={e => toggle(key, e.target.checked)} className="procurement-checkbox mt-0.5 shrink-0" />
      <span className="min-w-0 break-words">{t(`procurementAdmin.features.${key}`)}</span>
    </label>)}</div>
    <label className="block min-w-0 space-y-2 text-sm font-semibold leading-5 text-slate-700"><span className="block">{t('procurementAdmin.max_active_searches')}</span>
      <input aria-label={t('procurementAdmin.max_active_searches')} type="number" min="0" max="10000" step="1" value={value.max_active_searches}
        onChange={e => onChange({ ...value, max_active_searches: Math.min(10000, Math.max(0, Math.trunc(Number(e.target.value) || 0))) })}
        className="procurement-input w-full min-w-0 sm:max-w-xs" />
    </label>
    <fieldset className="min-w-0 border-t border-sky-100 pt-3 text-sm"><legend className="pr-2 font-semibold text-slate-700">{t('procurementAdmin.channels')}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">{(['site', 'telegram'] as const).map(channel => <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-1 text-slate-700" key={channel}>
        <input className="procurement-checkbox shrink-0" type="checkbox" checked={value.channels.includes(channel)} onChange={e => onChange({ ...value,
          channels: e.target.checked ? [...value.channels, channel] : value.channels.filter(c => c !== channel) })} />
        {t(`procurementAdmin.${channel}`)}
      </label>)}</div>
    </fieldset>
  </fieldset>;
}

export function ProcurementPlanSummary({ value }: { value?: ProcurementPlanConfig | null }) {
  const { t } = useLanguage();
  if (!value || !PROCUREMENT_FEATURES.some(key => value.features[key])) return null;
  return <ul className="min-w-0 space-y-2 text-sm leading-5" aria-label={t('procurementAdmin.services')}>
    {PROCUREMENT_FEATURES.filter(key => value.features[key]).map(key => <li key={key} className="flex items-start gap-2">
      <span className="shrink-0" aria-hidden="true">✓</span><span className="min-w-0 break-words">{t(`procurementAdmin.features.${key}`)}
      {key === 'search_alerts' && <strong className="whitespace-nowrap"> × {value.max_active_searches}</strong>}</span>
    </li>)}
    {value.features.search_alerts && <li className="break-words text-xs leading-5 opacity-80">{t('procurementAdmin.channels')}: {value.channels.map(c => t(`procurementAdmin.${c}`)).join(', ')}</li>}
  </ul>;
}
